import { useMatch } from "react-router-dom";
import { Clock } from "lucide-react";
import { useMigration } from "../api/hooks";
import { formatPauseAt } from "../api/types";
import { t } from "../i18n/t";

/**
 * F3 (C1, critique addendum): rendered ONCE, in `RootLayout`, not inside `StatusScreen`.
 * `screens/stageRoute.ts`'s `STAGE_OF_STATE` map sends `extracting`/`mapping` to Review, `profiling`/
 * `scoping` to Scope and `transforming`/`dry_running` to Dry run — only `loading`/`verifying` (plus the
 * terminal-ish `hypercare`/`locked`/`failed`/…) reach Status. A StatusScreen-only banner would therefore
 * never be seen for a cap during extraction, F3's headline case, so this renders for every
 * `/migrations/:id/*` screen instead.
 *
 * The redesign draws it as a full-width strip under the top bar (StatusFailed.html — without the design
 * annotation prefix, §8): warn fill, a `clock` icon, the pause sentence.
 */
export function MigrationPauseBanner(): JSX.Element | null {
  const match = useMatch("/migrations/:migrationId/*");
  const migrationId = match?.params.migrationId;
  // `/migrations/new(/consent)` matches this same pattern with `migrationId === "new"` — there is no real
  // migration yet, so `useMigration` is never even asked.
  const enabled = migrationId !== undefined && migrationId !== "new";
  const migration = useMigration(enabled ? migrationId : null);
  const pause = migration.data?.pause;
  if (!enabled || pause === undefined || pause === null) {
    return null;
  }
  const at = formatPauseAt(pause.until);
  const isFuture = new Date(pause.until).getTime() > Date.now();
  return (
    <div className="pausebar" data-testid="migration-paused" role="status">
      <div className="pausebar__inner">
        <span className="pausebar__icon" aria-hidden="true">
          <Clock size={18} strokeWidth={2} />
        </span>
        <span>
          {isFuture
            ? t("migration.paused", { at, vendor: pause.vendor })
            : t("migration.paused_past", { at, vendor: pause.vendor })}
          {pause.consecutive > 1 ? ` ${t("migration.paused_repeated", { n: pause.consecutive })}` : ""}
        </span>
      </div>
    </div>
  );
}
